import {LitElement, css, html} from "lit";
import {LANG_DEF} from "./profile-finance-grid-balance-sheet.js";
import {MAX_YEAR, MIN_YEAR} from "./profile-finance-grid.js";

export class ProfileFinancePNLChart extends LitElement {

	static get properties() {
		return {
			lang: { type: String, reflect: true },
			profileID: { type: String, attribute: "profile-id", reflect: true },
			loading: { type: Boolean, attribute: false },
			error: { type: String, attribute: false },
			year: { type: Number, attribute: false },
		};
	}

	get ChartContainer() {
		if(!this._chartContainer)
			this._chartContainer = this.shadowRoot.querySelector("#ChartContainer");
		return this._chartContainer;
	}

	constructor() {
		super();

		this.lang = "en";

		/**
		 * Next rows loading indicator. (Not for changing year view)
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * The error message when something went wrong and the grid can no longer show valid data.
		 *
		 * @type {string|null}
		 */
		this.error = null;

		/**
		 * The initial year.
		 *
		 * The columns and rows render depending on this value.
		 *
		 * The columns are composed out of this value: (year - 2) & (year) & (year + 2) which are 5 years in total, with
		 * the current year in the center.
		 *
		 * @type {number}
		 */
		this.year = new Date().getFullYear();

		this.chart = null;
	}

	attributeChangedCallback(name, old, value) {
		super.attributeChangedCallback(name, old, value);

		switch(name) {
			case "profile-id":
				if(old !== undefined)
					this.reset();
				if(value)
					requestAnimationFrame(() => this.refresh());
				break;
		}
	}

	reset() {
		this.loading = false;
		this.error = null;
		this.year = new Date().getFullYear();
	}

	refresh() {
		this.load(this.year - 2, this.year + 2);
	}

	/**
	 * Changes the view of the years by adding 1.
	 */
	nextYear() {
		// Because the table is always showing 5 years, and this.year is the middle one, we have to add 3 to check
		// the most right year not to exceed the limit.
		if(this.year+3 > MAX_YEAR)
			return;

		/*this.dispatchEvent(new CustomEvent("profile-finance-grid-year-change", {
			composed: true,
			detail: {
				target: this,
				direction: "next"
			}
		}));*/

		this.year++;
		this.refresh();
	}

	/**
	 * Changes the view of the years by subtracting 1.
	 */
	prevYear() {
		// Because the table is always showing 5 years, and this.year is the middle one, we have to subtract 3 to check
		// the most left year not to exceed the limit.
		if(this.year-3 < MIN_YEAR)
			return;

		/*this.dispatchEvent(new CustomEvent("profile-finance-grid-year-change", {
			composed: true,
			detail: {
				target: this,
				direction: "prev"
			}
		}));*/

		this.year--;
		this.refresh();
	}

	/**
	 * Loads the table data.
	 *
	 * The next rows will be loaded when fromYear and toYear is provided.
	 *
	 * Only the data for the column year is loaded when only fromYear is provided.
	 */
	load(fromYear, toYear = null) {
		this.error = null;

		if(!this.profileID) {
			this.error = "No Profile ID defined. " + this.profileID;
			return;
		}

		const query = `
			query ProfilePNLValues($profileID: ID!, $fromYear: Int!, $toYear: Int, $first: Int, $after: String) {
				listPNL(first: $first, after: $after) {
					edges {
						node {
							ID
							Type
							ProfileValues(profileID: $profileID, fromYear: $fromYear, toYear: $toYear, fillYears: true) {
								Year
								Value {Amount}
							}
						}
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`;

		const variables = {
			profileID: this.profileID,
			first: 10,
			fromYear: fromYear,
			toYear: toYear,
			fillYears: true
		};

		if(toYear !== null) {
			// Next loading
			this.loading = true;

			if(this._endCursor !== null) {
				variables.after = this._endCursor;

				// Reset loaded years, because if we load the next rows, we only have the loaded years of the
				// previous years and would not see the values of the new rows when changing the year view.
				this._loadedYears = {};
			}
		} else {
			// Skip, because year is already loaded.
			if(this._loadedYears.hasOwnProperty(fromYear))
				return;

			// Partial year update, load all years of previously loaded rows
			variables.first = this.data.length;
		}

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: query,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			const chartData = {
				fromYear: fromYear,
				EBITValues: [],
				RevenueValues: [],
			};

			const edges = response.data.listPNL.edges;
			if(edges.length > 0) {
				edges.forEach(item => {
					const pnl = item.node;

					if(pnl.Type === "EBIT") {
						pnl.ProfileValues.forEach((pnlValue, i) => {
							if(chartData.EBITValues[i] === undefined)
								chartData.EBITValues[i] = null;
							if(pnlValue.Value !== null)
								chartData.EBITValues[i] += parseFloat(pnlValue.Value.Amount);
						});
					} else if(pnl.Type === "REVENUE") {
						pnl.ProfileValues.forEach((pnlValue, i) => {
							if(chartData.RevenueValues[i] === undefined)
								chartData.RevenueValues[i] = null;
							if(pnlValue.Value !== null)
								chartData.RevenueValues[i] += parseFloat(pnlValue.Value.Amount);
						});
					}
				});
			}

			if(this.chart !== null)
				this.updateChart(chartData);
			else
				this.buildChart(chartData);
		})
		.catch(reason => this.error = reason)
		.finally(() => this.loading = false);
	}

	updateChart(data) {
		this.chart.series[0].update({pointStart: data.fromYear});
		this.chart.series[1].update({pointStart: data.fromYear});
		this.chart.series[0].setData(data.EBITValues);
		this.chart.series[1].setData(data.RevenueValues);
	}

	buildChart(data) {
		this.chart = Highcharts.chart(this.ChartContainer, {
			chart: {
				height: 130
			},

			title: {
				text: ""
			},

			subtitle: {
				text: ""
			},

			yAxis: {
				title: {
					text: ''
				}
			},

			/*xAxis: {
				accessibility: {
					rangeDescription: 'Range: 2010 to 2017'
				}
			},*/

			legend: {
				enabled: false
			},

			credits: {
				enabled: false
			},

			plotOptions: {
				series: {
					connectNulls: true,
					pointStart: data.fromYear
				}
			},

			colors: [
				"var(--aumentoo-highcharts-color0)",
				"var(--aumentoo-highcharts-color4)",
			],

			series: [{
				type: "line",
				name: LANG_DEF[this.lang]["type.EBIT"],
				data: data.EBITValues
			}, {
				type: "column",
				name: LANG_DEF[this.lang]["type.REVENUE"],
				data: data.RevenueValues
			}],
		});
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			.container {
				border-radius: 8px;
			}
		`;
	}

	render() {
		return html`<div id="ChartContainer" class="container"></div>`;
	}

}

customElements.define("profile-finance-pnl-chart", ProfileFinancePNLChart);